import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus,
  Pencil,
  Trash2,
  Check,
  Clock,
  MapPin,
  Users,
  Loader2,
  CheckCircle,
  Calendar,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import ImageSelector from "@/components/admin/ImageSelector";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminStages = () => {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStage, setEditingStage] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");

  const emptyForm = {
    title: { fr: "", en: "", ar: "", nl: "", es: "" },
    summary: { fr: "", en: "", ar: "", nl: "", es: "" },
    description: { fr: "", en: "", ar: "", nl: "", es: "" },
    category: "yoga",
    duration_days: 3,
    price: 500,
    max_participants: 12,
    location: "Désert de Chegaga",
    start_date: "",
    end_date: "",
    image_url: "",
    contact_email: "",
    contact_phone: "",
    website: "",
    includes: { fr: [], en: [] },
    is_published: false,
  };

  const [form, setForm] = useState(emptyForm);
  
  // Text inputs for list fields
  const [includesFr, setIncludesFr] = useState("");
  const [includesEn, setIncludesEn] = useState("");
  const [galleryInput, setGalleryInput] = useState("");

  useEffect(() => {
    fetchStages();
  }, []);

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` },
  });

  const fetchStages = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/admin/stages`, getAuthHeader());
      setStages(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (stage) => {
    setEditingStage(stage);
    setForm({
      ...emptyForm,
      ...stage,
      title: { ...emptyForm.title, ...stage.title },
      summary: { ...emptyForm.summary, ...stage.summary },
      description: { ...emptyForm.description, ...stage.description },
      includes: stage.includes || { fr: [], en: [] },
      start_date: stage.start_date || "",
      end_date: stage.end_date || "",
    });
    
    // Set list fields as text
    setIncludesFr(stage.includes?.fr?.join("\n") || "");
    setIncludesEn(stage.includes?.en?.join("\n") || "");
    setGalleryInput(stage.gallery?.join("\n") || "");
    
    setActiveTab("basic");
    setShowForm(true);
  };

  const handleCreate = () => {
    setEditingStage(null);
    setForm(emptyForm);
    setIncludesFr("");
    setIncludesEn("");
    setGalleryInput("");
    setActiveTab("basic");
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.title.fr || !form.contact_email) {
      toast.error("Veuillez remplir les champs requis (Titre FR et Email)");
      return;
    }

    const data = {
      ...form,
      includes: {
        fr: includesFr.split("\n").filter((s) => s.trim()),
        en: includesEn.split("\n").filter((s) => s.trim()) || includesFr.split("\n").filter((s) => s.trim()),
      },
      gallery: galleryInput.split("\n").filter((s) => s.trim()),
    };

    try {
      setSaving(true);
      if (editingStage) {
        await axios.put(
          `${API}/admin/stages/${editingStage.id}`,
          data,
          getAuthHeader()
        );
        toast.success("Stage mis à jour");
      } else {
        await axios.post(`${API}/admin/stages`, data, getAuthHeader());
        toast.success("Stage créé");
      }
      setShowForm(false);
      fetchStages();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleApprove = async (stageId) => {
    try {
      await axios.post(
        `${API}/admin/stages/${stageId}/approve`,
        {},
        getAuthHeader()
      );
      toast.success("Stage approuvé");
      fetchStages();
    } catch (err) {
      toast.error("Erreur");
    }
  };

  const handleDelete = async (stageId) => {
    if (!window.confirm("Supprimer ce stage ?")) return;
    try {
      await axios.delete(`${API}/admin/stages/${stageId}`, getAuthHeader());
      toast.success("Stage supprimé");
      fetchStages();
    } catch (err) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const categories = [
    { id: "yoga", name: "Yoga" },
    { id: "meditation", name: "Méditation" },
    { id: "art", name: "Art & Créativité" },
    { id: "music", name: "Musique" },
    { id: "wellness", name: "Bien-être" },
    { id: "spiritual", name: "Spiritualité" },
  ];

  return (
    <div data-testid="admin-stages-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-serif font-bold">Stages & Retraites</h1>
          <p className="text-muted-foreground">
            Gérez les stages et retraites proposés
          </p>
        </div>
        <Button onClick={handleCreate} data-testid="add-stage-btn">
          <Plus className="h-4 w-4 mr-2" />
          Nouveau stage
        </Button>
      </div>

      {/* Stages List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : stages.length === 0 ? (
        <div className="text-center py-12 bg-muted/30 rounded-xl">
          <p className="text-muted-foreground">Aucun stage</p>
        </div>
      ) : (
        <div className="grid gap-4">
          {stages.map((stage) => (
            <div
              key={stage.id}
              className={`bg-card border rounded-xl p-6 ${
                !stage.is_approved ? "border-amber-200" : ""
              }`}
              data-testid={`stage-item-${stage.id}`}
            >
              <div className="flex flex-col md:flex-row gap-4">
                {/* Image */}
                <div className="w-full md:w-32 h-24 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  <img
                    src={stage.image_url || "https://via.placeholder.com/200"}
                    alt={stage.title?.fr}
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <h3 className="font-semibold text-lg">
                        {stage.title?.fr}
                      </h3>
                      <p className="text-sm text-muted-foreground line-clamp-1">
                        {stage.summary?.fr}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      {stage.is_approved ? (
                        <Badge variant="default" className="bg-green-500">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Approuvé
                        </Badge>
                      ) : (
                        <Badge variant="secondary" className="bg-amber-100 text-amber-700">
                          <Clock className="h-3 w-3 mr-1" />
                          En attente
                        </Badge>
                      )}
                      <Badge variant="outline">{stage.category}</Badge>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-3">
                    <span className="flex items-center gap-1">
                      <Clock className="h-4 w-4" />
                      {stage.duration_days} jours
                    </span>
                    <span className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      {stage.max_participants} max
                    </span>
                    <span className="flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {stage.location}
                    </span>
                    {stage.start_date && (
                      <span className="flex items-center gap-1">
                        <Calendar className="h-4 w-4" />
                        {stage.start_date}
                      </span>
                    )}
                    <span className="font-semibold text-primary">
                      {stage.price}€
                    </span>
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">
                      Par: {stage.collaborator_name || "Admin"}
                    </span>
                    <div className="flex gap-2">
                      {!stage.is_approved && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-green-600"
                          onClick={() => handleApprove(stage.id)}
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Approuver
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(stage)}
                        data-testid={`edit-stage-${stage.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive"
                        onClick={() => handleDelete(stage.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit/Create Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingStage ? "Modifier le stage" : "Nouveau stage"}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Infos de base</TabsTrigger>
              <TabsTrigger value="content">Contenu</TabsTrigger>
              <TabsTrigger value="media">Médias</TabsTrigger>
              <TabsTrigger value="translations">Traductions</TabsTrigger>
            </TabsList>

            {/* TAB: Infos de base */}
            <TabsContent value="basic" className="space-y-4 mt-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Titre (FR) *</Label>
                  <Input
                    value={form.title.fr}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        title: { ...form.title, fr: e.target.value },
                      })
                    }
                  />
                </div>
                <div>
                  <Label>Titre (EN)</Label>
                  <Input
                    value={form.title.en}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        title: { ...form.title, en: e.target.value },
                      })
                    }
                  />
                </div>
              </div>

              {/* Summary */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Résumé court (FR)</Label>
                  <Textarea
                    value={form.summary.fr}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        summary: { ...form.summary, fr: e.target.value },
                      })
                    }
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Résumé court (EN)</Label>
                  <Textarea
                    value={form.summary.en}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        summary: { ...form.summary, en: e.target.value },
                      })
                    }
                    rows={2}
                  />
                </div>
              </div>

              <Separator />

              {/* Category & Details */}
              <div className="grid md:grid-cols-4 gap-4">
                <div>
                  <Label>Catégorie</Label>
                  <Select
                    value={form.category}
                    onValueChange={(v) => setForm({ ...form, category: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Durée (jours)</Label>
                  <Input
                    type="number"
                    value={form.duration_days}
                    onChange={(e) =>
                      setForm({ ...form, duration_days: parseInt(e.target.value) || 1 })
                    }
                  />
                </div>
                <div>
                  <Label>Prix (€)</Label>
                  <Input
                    type="number"
                    value={form.price}
                    onChange={(e) =>
                      setForm({ ...form, price: parseFloat(e.target.value) || 0 })
                    }
                  />
                </div>
                <div>
                  <Label>Participants max</Label>
                  <Input
                    type="number"
                    value={form.max_participants}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        max_participants: parseInt(e.target.value) || 1,
                      })
                    }
                  />
                </div>
              </div>

              {/* Location */}
              <div>
                <Label>Lieu</Label>
                <Input
                  value={form.location}
                  onChange={(e) => setForm({ ...form, location: e.target.value })}
                />
              </div>

              {/* Dates */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Date de début</Label>
                  <Input
                    type="date"
                    value={form.start_date}
                    onChange={(e) =>
                      setForm({ ...form, start_date: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Date de fin</Label>
                  <Input
                    type="date"
                    value={form.end_date}
                    onChange={(e) =>
                      setForm({ ...form, end_date: e.target.value })
                    }
                  />
                </div>
              </div>

              <Separator />

              {/* Contact */}
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label>Email contact *</Label>
                  <Input
                    type="email"
                    value={form.contact_email}
                    onChange={(e) =>
                      setForm({ ...form, contact_email: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Téléphone</Label>
                  <Input
                    value={form.contact_phone}
                    onChange={(e) =>
                      setForm({ ...form, contact_phone: e.target.value })
                    }
                  />
                </div>
                <div>
                  <Label>Site web</Label>
                  <Input
                    value={form.website}
                    onChange={(e) => setForm({ ...form, website: e.target.value })}
                  />
                </div>
              </div>
            </TabsContent>

            {/* TAB: Contenu */}
            <TabsContent value="content" className="space-y-4 mt-4">
              {/* Full Description */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Description complète (FR)</Label>
                  <Textarea
                    value={form.description.fr}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        description: { ...form.description, fr: e.target.value },
                      })
                    }
                    rows={5}
                  />
                </div>
                <div>
                  <Label>Description complète (EN)</Label>
                  <Textarea
                    value={form.description.en}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        description: { ...form.description, en: e.target.value },
                      })
                    }
                    rows={5}
                  />
                </div>
              </div>

              <Separator />

              {/* Includes */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Ce qui est inclus (FR) - un élément par ligne</Label>
                  <Textarea
                    value={includesFr}
                    onChange={(e) => setIncludesFr(e.target.value)}
                    rows={5}
                    placeholder="Hébergement en bivouac&#10;Tous les repas&#10;Transport depuis Marrakech&#10;Guide spécialisé"
                  />
                </div>
                <div>
                  <Label>Ce qui est inclus (EN) - un élément par ligne</Label>
                  <Textarea
                    value={includesEn}
                    onChange={(e) => setIncludesEn(e.target.value)}
                    rows={5}
                    placeholder="Bivouac accommodation&#10;All meals&#10;Transport from Marrakech&#10;Specialized guide"
                  />
                </div>
              </div>
            </TabsContent>

            {/* TAB: Médias */}
            <TabsContent value="media" className="space-y-4 mt-4">
              <ImageSelector
                value={form.image_url}
                onChange={(url) => setForm({ ...form, image_url: url })}
                label="Image principale du stage"
              />

              <Separator />

              <div>
                <Label>Galerie d'images (URLs) - une URL par ligne</Label>
                <Textarea
                  value={galleryInput}
                  onChange={(e) => setGalleryInput(e.target.value)}
                  rows={6}
                  placeholder="https://exemple.com/image1.jpg&#10;https://exemple.com/image2.jpg"
                />
                <p className="text-sm text-muted-foreground mt-1">
                  Entrez les URLs des images supplémentaires, une par ligne
                </p>
              </div>
            </TabsContent>

            {/* TAB: Traductions */}
            <TabsContent value="translations" className="space-y-4 mt-4">
              <p className="text-sm text-muted-foreground mb-4">
                Traductions additionnelles (Arabe, Néerlandais, Espagnol)
              </p>

              <div className="space-y-4">
                <div>
                  <Label>Titre (AR - Arabe)</Label>
                  <Input
                    value={form.title.ar || ""}
                    onChange={(e) =>
                      setForm({ ...form, title: { ...form.title, ar: e.target.value } })
                    }
                    dir="rtl"
                  />
                </div>
                <div>
                  <Label>Résumé (AR - Arabe)</Label>
                  <Textarea
                    value={form.summary.ar || ""}
                    onChange={(e) =>
                      setForm({ ...form, summary: { ...form.summary, ar: e.target.value } })
                    }
                    rows={2}
                    dir="rtl"
                  />
                </div>
                <div>
                  <Label>Description (AR - Arabe)</Label>
                  <Textarea
                    value={form.description.ar || ""}
                    onChange={(e) =>
                      setForm({ ...form, description: { ...form.description, ar: e.target.value } })
                    }
                    rows={3}
                    dir="rtl"
                  />
                </div>
              </div>

              <Separator />

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Titre (NL - Néerlandais)</Label>
                  <Input
                    value={form.title.nl || ""}
                    onChange={(e) =>
                      setForm({ ...form, title: { ...form.title, nl: e.target.value } })
                    }
                  />
                </div>
                <div>
                  <Label>Titre (ES - Espagnol)</Label>
                  <Input
                    value={form.title.es || ""}
                    onChange={(e) =>
                      setForm({ ...form, title: { ...form.title, es: e.target.value } })
                    }
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Résumé (NL - Néerlandais)</Label>
                  <Textarea
                    value={form.summary.nl || ""}
                    onChange={(e) =>
                      setForm({ ...form, summary: { ...form.summary, nl: e.target.value } })
                    }
                    rows={2}
                  />
                </div>
                <div>
                  <Label>Résumé (ES - Espagnol)</Label>
                  <Textarea
                    value={form.summary.es || ""}
                    onChange={(e) =>
                      setForm({ ...form, summary: { ...form.summary, es: e.target.value } })
                    }
                    rows={2}
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Description (NL - Néerlandais)</Label>
                  <Textarea
                    value={form.description.nl || ""}
                    onChange={(e) =>
                      setForm({ ...form, description: { ...form.description, nl: e.target.value } })
                    }
                    rows={3}
                  />
                </div>
                <div>
                  <Label>Description (ES - Espagnol)</Label>
                  <Textarea
                    value={form.description.es || ""}
                    onChange={(e) =>
                      setForm({ ...form, description: { ...form.description, es: e.target.value } })
                    }
                    rows={3}
                  />
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setShowForm(false)}>
              Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {editingStage ? "Mettre à jour" : "Créer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminStages;
